import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { TaskAssignable, TaskDetail } from "@dentacore/shared-types";

import {
  actOnTask,
  assignTask,
  closeTask,
  getTask,
  noteOnTask,
  readAssignable,
  snoozeTask,
} from "../api/tasks";
import { Modal } from "../ui/modal";
import {
  Holder,
  KindChip,
  RECORD_KIND,
  SourceBadge,
  initials,
  recordLink,
  recordName,
  when,
} from "./task-bits";
import styles from "./tasks.module.css";

/** The snooze choices (TK-D6), worked out from now on this browser's clock. */
export function snoozeChoices(now: Date): { key: string; label: string; hint: string; at: Date }[] {
  const later = new Date(now);
  later.setHours(now.getHours() + 3, 0, 0, 0);
  const tomorrow = new Date(now);
  tomorrow.setDate(now.getDate() + 1);
  tomorrow.setHours(8, 0, 0, 0);
  const monday = new Date(now);
  monday.setDate(now.getDate() + ((8 - now.getDay()) % 7 || 7));
  monday.setHours(8, 0, 0, 0);
  const hm = (d: Date) => d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  return [
    { key: "later", label: "Later today", hint: hm(later), at: later },
    { key: "tomorrow", label: "Tomorrow morning", hint: hm(tomorrow), at: tomorrow },
    {
      key: "monday",
      label: "Next Monday",
      hint: monday.toLocaleDateString("en-GB", { day: "numeric", month: "short" }),
      at: monday,
    },
  ];
}

/** `YYYY-MM-DDTHH:mm` for a datetime-local field, on this browser's clock. */
function localInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * One task (TK-D4): its description, the record it is about with a link to
 * that record's own screen (TK-D37), notes and history, and what can be done
 * with it — Take it, Give back, reassign, Snooze, Close with a reason,
 * Resolve, Reopen. Every action answers with the task as it now stands.
 */
export function TaskDialog({
  taskId,
  start,
  onClose,
  onChanged,
}: {
  taskId: string;
  /**
   * Where the quick ring asked it to open (STORY-221): on *Close*'s reason
   * field, or on the note field. The same controls, already pressed.
   */
  start?: "close" | "note";
  onClose: () => void;
  onChanged: () => void;
}): React.JSX.Element {
  const [task, setTask] = useState<TaskDetail | null>(null);
  const [who, setWho] = useState<TaskAssignable | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [closing, setClosing] = useState(start === "close");
  const [reason, setReason] = useState("");
  const [snoozing, setSnoozing] = useState(false);
  const [pick, setPick] = useState(() => localInput(new Date(Date.now() + 2 * 24 * 3600_000)));

  useEffect(() => {
    const controller = new AbortController();
    getTask(taskId, controller.signal)
      .then((found) => {
        setTask(found);
        return readAssignable(found.branchId, controller.signal).then(setWho);
      })
      .catch((cause: unknown) => {
        if (!controller.signal.aborted)
          setError(cause instanceof Error ? cause.message : "Could not open the task");
      });
    return () => controller.abort();
  }, [taskId]);

  /** Run one action; show the task as it now stands, and have the list re-read. */
  const run = (action: () => Promise<TaskDetail>, after?: () => void): void => {
    setBusy(true);
    setError(null);
    action()
      .then((next) => {
        setTask(next);
        after?.();
        onChanged();
      })
      .catch((cause: unknown) =>
        setError(cause instanceof Error ? cause.message : "That did not work"),
      )
      .finally(() => setBusy(false));
  };

  const now = new Date();
  const open = task?.status === "OPEN";
  const asleep = task !== null && task.snoozedUntil !== null && new Date(task.snoozedUntil) > now;
  const link = task === null ? null : recordLink(task);

  return (
    <Modal
      label={task?.title ?? "Task"}
      onClose={onClose}
      panelClassName={styles.dialog}
      testId="task-dialog"
    >
      {task === null ? (
        <div className={styles.dialogMain}>
          {error === null ? (
            <p className={styles.footNote}>Opening…</p>
          ) : (
            <p className={styles.error}>{error}</p>
          )}
        </div>
      ) : (
        <>
          <header className={styles.dialogHead}>
            <div className={styles.line}>
              <KindChip task={task} />
              <SourceBadge task={task} />
              {!open && (
                <span
                  className={`${styles.outcome} ${task.status === "RESOLVED" ? (styles.outcomeDone ?? "") : task.status === "AUTO_CLOSED" ? (styles.outcomeAuto ?? "") : (styles.outcomeClosed ?? "")}`}
                >
                  {task.status === "RESOLVED"
                    ? "Resolved"
                    : task.status === "AUTO_CLOSED"
                      ? "Closed by DentaCore"
                      : "Closed"}
                </span>
              )}
              {open && asleep && task.snoozedUntil !== null && (
                <span className={`${styles.outcome} ${styles.outcomeAuto ?? ""}`}>
                  Snoozed until {when(task.snoozedUntil, now)}
                </span>
              )}
              <span className={styles.grow} />
              <button
                type="button"
                className={styles.quiet}
                onClick={onClose}
                aria-label="Close the task"
              >
                ×
              </button>
            </div>
            <h2 className={styles.dialogTitle}>{task.title}</h2>
            <div className={styles.line}>
              <Holder task={task} />
              {task.canTake && (
                <button
                  type="button"
                  className={`${styles.primary} ${styles.small}`}
                  disabled={busy}
                  onClick={() => run(() => actOnTask(task.id, "take"))}
                  data-testid="task-take"
                >
                  Take it
                </button>
              )}
              {task.canGiveBack && task.role !== null && (
                <button
                  type="button"
                  className={`${styles.ghost} ${styles.small}`}
                  disabled={busy}
                  onClick={() => run(() => actOnTask(task.id, "give-back"))}
                  data-testid="task-give-back"
                >
                  Give back to {task.role.name}
                </button>
              )}
              {open && who !== null && (
                <select
                  className={styles.filterSelect}
                  aria-label="Give it to someone else"
                  value=""
                  disabled={busy}
                  onChange={(event) => {
                    const [kind, id] = event.target.value.split(":");
                    if (id === undefined) return;
                    run(() =>
                      assignTask(task.id, kind === "role" ? { roleId: id } : { assigneeId: id }),
                    );
                  }}
                  data-testid="task-assign"
                >
                  <option value="">Give it to…</option>
                  <optgroup label="A role (anyone in it can take it)">
                    {who.roles.map((r) => (
                      <option key={r.id} value={`role:${r.id}`}>
                        {r.name}
                      </option>
                    ))}
                  </optgroup>
                  <optgroup label="A person working here">
                    {who.people.map((p) => (
                      <option key={p.id} value={`person:${p.id}`}>
                        {p.name}
                      </option>
                    ))}
                  </optgroup>
                </select>
              )}
            </div>
            <div className={styles.meta}>
              <span>
                {task.dueAt === null
                  ? "No due date"
                  : `${new Date(task.dueAt) < now && open ? "Overdue · due" : "Due"} ${when(task.dueAt, now)}`}
              </span>
              <span>
                Created {when(task.createdAt, now)} by {task.createdBy?.name ?? "DentaCore"}
              </span>
            </div>
          </header>

          <div className={styles.dialogBody}>
            <div className={styles.dialogMain}>
              <section className={styles.section}>
                <h3 className={styles.sectionTitle}>Description</h3>
                <p className={styles.text}>
                  {task.description === "" ? "No description." : task.description}
                </p>
              </section>

              <section className={styles.section}>
                <h3 className={styles.sectionTitle}>Notes and activity</h3>
                <div className={styles.notes} data-testid="task-notes">
                  {task.notes.map((n) => (
                    <div
                      key={n.id}
                      className={
                        n.kind === "EVENT" ? `${styles.note} ${styles.event}` : styles.note
                      }
                    >
                      <span
                        className={`${styles.avatar} ${n.author === null ? (styles.avatarRole ?? "") : ""}`}
                        aria-hidden="true"
                      >
                        {n.author === null ? "D" : initials(n.author.name)}
                      </span>
                      <div>
                        <div className={styles.noteHead}>
                          {n.kind === "NOTE" && n.author !== null ? `${n.author.name} · ` : ""}
                          {when(n.createdAt, now)}
                        </div>
                        <div className={styles.noteBody}>{n.body}</div>
                      </div>
                    </div>
                  ))}
                </div>
                {open && (
                  <div className={styles.addNote}>
                    <textarea
                      className={styles.textarea}
                      rows={2}
                      value={note}
                      placeholder="Write a note for the team"
                      onChange={(event) => setNote(event.target.value)}
                      aria-label="A note"
                      autoFocus={start === "note"}
                      data-testid="task-note"
                    />
                    <button
                      type="button"
                      className={`${styles.ghost} ${styles.small}`}
                      disabled={busy || note.trim() === ""}
                      onClick={() =>
                        run(
                          () => noteOnTask(task.id, note.trim()),
                          () => setNote(""),
                        )
                      }
                      data-testid="task-note-add"
                    >
                      Add note
                    </button>
                  </div>
                )}
              </section>
            </div>

            <aside className={styles.dialogAside}>
              <div className={styles.record}>
                {task.subject === null ? (
                  <>
                    <span className={styles.recordKind}>Related to</span>
                    <p className={styles.text}>Nothing. This task stands on its own.</p>
                  </>
                ) : (
                  <>
                    <span className={styles.recordKind}>{RECORD_KIND[task.subject.kind]}</span>
                    <p className={styles.recordName}>{recordName(task)}</p>
                    {link !== null && (
                      <Link
                        to={link.to}
                        className={`${styles.ghost} ${styles.small}`}
                        data-testid="task-record-link"
                      >
                        {link.label}
                      </Link>
                    )}
                    {task.source === "AUTO" && open && (
                      <p className={styles.footNote}>
                        This task closes itself when the record is put right there.
                      </p>
                    )}
                  </>
                )}
              </div>
            </aside>
          </div>

          <footer className={styles.dialogFoot}>
            {error !== null && (
              <p className={styles.error} role="alert">
                {error}
              </p>
            )}
            {!open ? (
              <>
                <span className={styles.footNote}>
                  {task.status === "CLOSED"
                    ? `Closed: ${task.closeReason ?? ""}`
                    : task.status === "AUTO_CLOSED"
                      ? "Closed by DentaCore."
                      : `Resolved by ${task.doneBy?.name ?? "someone"}.`}
                </span>
                <span className={styles.grow} />
                <button
                  type="button"
                  className={styles.ghost}
                  disabled={busy}
                  onClick={() => run(() => actOnTask(task.id, "reopen"))}
                  data-testid="task-reopen"
                >
                  Reopen
                </button>
              </>
            ) : closing ? (
              <div className={styles.reason}>
                <input
                  className={styles.input}
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  placeholder="Why is it not needed?"
                  aria-label="Why it is not needed"
                  autoFocus={start === "close"}
                  data-testid="task-close-reason"
                />
                <button
                  type="button"
                  className={styles.danger}
                  disabled={busy || reason.trim() === ""}
                  onClick={() =>
                    run(
                      () => closeTask(task.id, reason.trim()),
                      () => {
                        setClosing(false);
                        setReason("");
                      },
                    )
                  }
                  data-testid="task-close-confirm"
                >
                  Close task
                </button>
                <button type="button" className={styles.quiet} onClick={() => setClosing(false)}>
                  Cancel
                </button>
              </div>
            ) : (
              <>
                <div className={styles.menuWrap}>
                  <button
                    type="button"
                    className={styles.ghost}
                    aria-expanded={snoozing}
                    onClick={() => setSnoozing((was) => !was)}
                    data-testid="task-snooze"
                  >
                    Snooze
                  </button>
                  {snoozing && (
                    <div className={styles.menu} role="menu">
                      {snoozeChoices(now).map((choice) => (
                        <button
                          key={choice.key}
                          type="button"
                          role="menuitem"
                          className={styles.menuItem}
                          onClick={() =>
                            run(
                              () => snoozeTask(task.id, choice.at.toISOString()),
                              () => setSnoozing(false),
                            )
                          }
                          data-testid={`task-snooze-${choice.key}`}
                        >
                          {choice.label}
                          <span className={styles.menuHint}>{choice.hint}</span>
                        </button>
                      ))}
                      <div className={styles.menuCustom}>
                        <input
                          type="datetime-local"
                          className={styles.input}
                          value={pick}
                          onChange={(event) => setPick(event.target.value)}
                          aria-label="Snooze until"
                        />
                        <button
                          type="button"
                          className={`${styles.ghost} ${styles.small}`}
                          onClick={() =>
                            run(
                              () => snoozeTask(task.id, new Date(pick).toISOString()),
                              () => setSnoozing(false),
                            )
                          }
                        >
                          Snooze
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                {asleep && (
                  <button
                    type="button"
                    className={styles.ghost}
                    disabled={busy}
                    onClick={() => run(() => actOnTask(task.id, "wake"))}
                    data-testid="task-wake"
                  >
                    Wake now
                  </button>
                )}
                <span className={styles.grow} />
                <button
                  type="button"
                  className={styles.danger}
                  onClick={() => {
                    setClosing(true);
                    setSnoozing(false);
                  }}
                  data-testid="task-close"
                >
                  Close
                </button>
                <button
                  type="button"
                  className={styles.primary}
                  disabled={busy}
                  onClick={() => run(() => actOnTask(task.id, "resolve"))}
                  data-testid="task-resolve"
                >
                  Resolve
                </button>
              </>
            )}
          </footer>
        </>
      )}
    </Modal>
  );
}
